import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 300000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sd_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default api;

const saveToken = (data) => {
  if (data?.session_token) localStorage.setItem('sd_token', data.session_token);
  return data;
};

export const auth = {
  linkedinUrl: () => api.get('/auth/linkedin/url').then(r => r.data),
  linkedinCallback: (code, state) => api.post('/auth/linkedin/callback', { code, state }).then(r => saveToken(r.data)),
  devLogin: (email, name) => api.post('/auth/dev-login', { email, name }).then(r => saveToken(r.data)),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }).then(r => saveToken(r.data)),
  login: (email, password) => api.post('/auth/login', { email, password }).then(r => saveToken(r.data)),
  setPassword: (password) => api.post('/auth/set-password', { password }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => { localStorage.removeItem('sd_token'); return r.data; }),
};

export const profile = {
  extractCV: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/profile/extract-cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  extractCombined: ({ linkedinFile, cvFile }) => {
    const fd = new FormData();
    if (linkedinFile) fd.append('linkedin_file', linkedinFile);
    if (cvFile) fd.append('cv_file', cvFile);
    return api.post('/profile/extract-combined', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  extractText: (text, source = 'paste') => api.post('/profile/extract-text', { text, source }).then(r => r.data),
  save: (data) => api.post('/profile', data).then(r => r.data),
  get: () => api.get('/profile').then(r => r.data),
  reset: () => api.post('/profile/reset').then(r => r.data),
  newsletterConsent: ({ newsletter_opt_in, linkedin_follow_intent, source = 'onboarding' }) =>
    api.post('/profile/newsletter-consent', { newsletter_opt_in, linkedin_follow_intent, source }).then(r => r.data),
};

export const analysis = {
  runAsync: (opts) => api.post('/analysis/run-async', opts || {}).then(r => r.data),
  refreshMarket: (opts) => api.post('/analysis/refresh-market', opts || {}).then(r => r.data),
  coverLetter: (opts) => api.post('/analysis/cover-letter', opts || {}).then(r => r.data),
  saveCoverLetter: (opts) => api.post('/analysis/cover-letter/save', opts).then(r => r.data),
  listCoverLetters: () => api.get('/analysis/cover-letter/history').then(r => r.data),
  deleteCoverLetter: (letterId) => api.delete(`/analysis/cover-letter/history/${letterId}`).then(r => r.data),
  latest: ({ marketKey, pivotType } = {}) => api.get('/analysis/latest', {
    params: {
      ...(marketKey ? { market_key: marketKey } : {}),
      ...(pivotType ? { pivot_type: pivotType } : {}),
    },
  }).then(r => r.data),
  markets: () => api.get('/analysis/markets').then(r => r.data),
  pivots: () => api.get('/analysis/pivots').then(r => r.data),
  deleteMarket: (marketKey) => api.delete(`/analysis/markets/${encodeURIComponent(marketKey)}`).then(r => r.data),
  jdAsync: (jd_text) => api.post('/analysis/jd-async', { jd_text }).then(r => r.data),
  jobStatus: (job_id) => api.get(`/analysis/jobs/${job_id}`).then(r => r.data),
  jdList: () => api.get('/analysis/jd').then(r => r.data),
  jdGet: (id) => api.get(`/analysis/jd/${id}`).then(r => r.data),
  jdDelete: (id) => api.delete(`/analysis/jd/${id}`).then(r => r.data),
  jdRegenerateLinkedIn: (id) => api.post(`/analysis/jd/${id}/regenerate-linkedin`).then(r => r.data),
  jdCvAuditUpload: (id, file) => {
    // LEGACY sync path — retained for backward compat. Prefer jdCvAuditUploadAsync.
    const fd = new FormData();
    fd.append('cv_file', file);
    return api.post(`/analysis/jd/${id}/cv-audit`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    }).then(r => r.data);
  },
  // Fire-and-forget the CV audit as a background job. Returns { job_id }.
  // The audit continues running server-side even if the user navigates away
  // or the browser tab is closed. Poll via `analysis.jobStatus(job_id)` or
  // resume via `analysis.jdCvAuditActive(jdId)` on remount.
  jdCvAuditUploadAsync: (id, file) => {
    const fd = new FormData();
    fd.append('cv_file', file);
    return api.post(`/analysis/jd/${id}/cv-audit-async`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,  // upload only — the actual audit runs on the server
    }).then(r => r.data);
  },
  // Returns { job_id: string|null, status } — the most recent in-flight
  // cv_audit job for this JD, if any. Used to resume polling after a tab
  // switch or full page reload.
  jdCvAuditActive: (id) => api.get(`/analysis/jd/${id}/cv-audit/active`).then(r => r.data),
};

export const progress = {
  get: () => api.get('/progress').then(r => r.data),
  update: (payload) => api.put('/progress', payload).then(r => r.data),
  // Auto-tracked Quick Win completion. Idempotent server-side; safe to call
  // repeatedly. Returns { ok, quick_wins, newly_completed } so the UI can
  // decide whether to fire confetti this time.
  markQuickWin: (key) => api.post(`/progress/quick-win/${encodeURIComponent(key)}`).then(r => r.data),
};

export async function pollJob(job_id, { onProgress, intervalMs = 3000, timeoutMs = 600000 } = {}) {
  // Resilient polling that survives:
  //   - Mobile tab-backgrounding (iOS Safari throttles timers + aborts in-flight fetches)
  //   - Transient network hiccups (Cloudflare 5xx, DNS blips, LTE→WiFi handoff)
  //   - The `visibilitychange` → immediate re-check pattern (see AnalysisLoading)
  // We tolerate up to CONSECUTIVE_FAIL_LIMIT status-endpoint failures in a row
  // before propagating the error; single failures are silently retried on the
  // next tick. Users who put the phone away mid-analysis come back to a still-
  // running progress bar, not an error screen.
  const CONSECUTIVE_FAIL_LIMIT = 6;
  const start = Date.now();
  let consecutiveFailures = 0;
  let lastVisibleKick = 0;
  const onVisibleKick = () => {
    // When the tab becomes visible again, force an immediate poll (bypass the
    // interval wait). Guarded so we can't stampede if visibility flaps.
    if (document.visibilityState === 'visible') {
      lastVisibleKick = Date.now();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibleKick);
  }
  try {
    while (Date.now() - start < timeoutMs) {
      let j;
      try {
        j = await analysis.jobStatus(job_id);
        consecutiveFailures = 0;
      } catch (err) {
        consecutiveFailures += 1;
        // Only give up if we've failed CONSECUTIVE_FAIL_LIMIT times in a row
        // AND the tab is currently visible (otherwise we're just throttled).
        const tabVisible = typeof document === 'undefined' || document.visibilityState === 'visible';
        if (consecutiveFailures >= CONSECUTIVE_FAIL_LIMIT && tabVisible) {
          throw err;
        }
        // Back off a bit on repeated failures, cap at 8s
        const backoff = Math.min(8000, intervalMs * (1 + consecutiveFailures * 0.5));
        await new Promise(r => setTimeout(r, backoff));
        continue;
      }
      onProgress?.(j);
      if (j.status === 'done') return j;
      if (j.status === 'error') throw new Error(j.error || 'Job failed');
      // Short wait; if visibility kick just fired, wait only a tick
      const wait = (Date.now() - lastVisibleKick < 500) ? 100 : intervalMs;
      await new Promise(r => setTimeout(r, wait));
    }
    throw new Error('Job timed out');
  } finally {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibleKick);
    }
  }
}
